<template>
  <base-pitch-type-card
    :available-to-search="availableToSearch"
    :pitchtype="pitchtype"
    :calendar-start="calendarStart"
    :campsite-slug="campsiteSlug"
    :campsite-categories="campsiteCategories"
    :party="party"
    :is-available="isAvailable"
    :dates-range="dateRange"
    :calendar-start-date="calendarStartDate"
    @datesChanged="onDatesChanged"
  >
    <template #state>
      <charge-type-state :pitchtype="pitchtype" />
    </template>

    <template #status-msg>
      <div class="undated-status" :class="{ desktop: $isDesktop }">
        <div v-if="isAvailable && isLowAvailability" class="status">
          <img src="@/assets/images/timer.svg" alt="" width="9" height="15" />
          <translate :tt="{ pitchesToSell }">
            Only {pitchesToSell} left on our site
          </translate>
        </div>
        <div v-if="offersActive" class="status">
          <img
            src="@/assets/images/star-outline.svg"
            alt=""
            width="15"
            height="14"
          />
          <translate :tt="{ discount: offerPercentage / 100 }">
            {discount, number, percent} off
          </translate>
        </div>
      </div>
    </template>
  </base-pitch-type-card>
</template>

<script lang="ts">
import { computed, provide } from 'vue'
import { addDays } from '~/utility/date/relative'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { logger } from '../../../../utility/logger'
import {
  CalendarDay,
  fetchAlternativeStays,
} from '../../../campsite-details/alternativeStaysFetch'
import { PaymentTiming } from '../../../campsite-details/types'
import { Party, PitchTypeWithChargeType } from '../../types'

@Component
class PitchTypeCardDated extends Vue {
  @Prop()
  availableToSearch: boolean

  @Prop()
  campsiteName: string

  @Prop()
  campsiteCategories: CampsiteCategory[]

  @Prop()
  campsiteSlug: string

  @Prop()
  campsiteHierarchyPath: string

  @Prop()
  campsiteAllowOverrideImmediate: boolean

  @Prop()
  campsitePaymentTiming: PaymentTiming

  @Prop()
  campsiteRefundPolicy: string

  @Prop()
  campsiteUsefulInfo: string

  @Prop()
  campsiteTermsUsefulInfo: string[]

  @Prop()
  party: Party

  @Prop()
  dateRange: { start: string; end: string }

  @Prop()
  pitchtype: PitchTypeWithChargeType

  @Prop()
  calendarStartDate: string

  bookingIsDisabled = false

  alternativeStaysData: CalendarDay[] = []
  alternativeStaysLoading = true
  alternativeStaysError: Error | null = null
  lastFetchParams: Record<string, string> | null = null

  onDatesChanged(dates: Date[]) {
    this.bookingIsDisabled = dates.length !== 2
  }

  get chargetype() {
    return this.pitchtype.chargetype
  }

  get calendarStart() {
    return addDays(-7, this.dateRange.start)
  }

  get isAvailable() {
    return this.chargetype.state === 'available'
  }

  get isLowAvailability() {
    return this.pitchesToSell < 10
  }

  get pitchesToSell() {
    return this.chargetype.pitchesToSell
  }

  get offersActive() {
    return this.chargetype.quote?.offersActive
  }

  get offerPercentage() {
    return this.chargetype.quote!.offersApplied[0].discountPercent
  }

  created() {
    this.provideAlternativeStays()
  }

  async mounted() {
    await this.checkChargetypeState()
  }

  provideAlternativeStays() {
    provide('alternativeStays', {
      data: computed(() => this.alternativeStaysData),
      loading: computed(() => this.alternativeStaysLoading),
      error: computed(() => this.alternativeStaysError),
    })
  }

  @Watch('chargetype', { deep: true })
  async onChargetypeChange() {
    await this.checkChargetypeState()
  }

  async checkChargetypeState() {
    if (
      this.chargetype?.state === 'unavailable' &&
      !this.chargetype?.isOvercapacityMessage
    ) {
      await this.fetchAlternativeStaysData()
    } else {
      this.alternativeStaysLoading = false
      this.lastFetchParams = null
      this.alternativeStaysData = []
    }
  }

  async fetchAlternativeStaysData() {
    const currentParams = {
      pitchtypeId: this.pitchtype.id,
      start: this.dateRange.start,
      end: this.dateRange.end,
      adults: String(this.$route.query.adults || '2'),
      childAges: String(this.$route.query.child_ages || ''),
      lang: this.$route.params.lang || 'en-gb',
    }
    // Check if params have changed
    if (
      this.lastFetchParams &&
      JSON.stringify(this.lastFetchParams) === JSON.stringify(currentParams)
    ) {
      logger('Skipping alternativeStaysData fetch - params unchanged')
      return
    }
    this.lastFetchParams = currentParams
    logger('fetching alternativeStaysData', currentParams)
    this.alternativeStaysLoading = true
    this.alternativeStaysError = null
    try {
      this.alternativeStaysData = await fetchAlternativeStays(currentParams)
      logger('alternativeStaysData fetched', this.alternativeStaysData)
    } catch (error) {
      this.alternativeStaysError = error as Error
    } finally {
      this.alternativeStaysLoading = false
    }
  }
}

export default toNative(PitchTypeCardDated)
</script>
<style scoped>
.status {
  color: var(--colour-offers);
  font-size: var(--small);
  display: flex;
  line-height: 90%;
}

.status img {
  width: auto;
  height: 1em;
  margin: -0.1em 0.25em 0 0;
}

.undated-status {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  justify-content: flex-start;
}

.status + .status {
  margin-top: 0.3em;
  white-space: nowrap;
}

/* desktop & tablet */
.desktop .status,
.desktop .status + .status {
  margin: 0;
}

.undated-status.desktop {
  align-items: start;
  column-gap: 1em;
  flex-direction: row;
  margin-top: 0;
}
</style>
